import { useEffect, useState } from 'react';

// Libs
import CommonDataTable from '@libComponents/table/CommonDataTable';
import { createFetchPaginatedDataset, datasetLoadingSelector, entitySelector } from '@libs/datasets';
import { Box, TableBody, TableRow, Avatar, TableCell, styled } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';

import createCachedSelector from 're-reselect';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { DOMAIN_NAME, ROWS_PER_PAGE, ROWS_PER_PAGE_OPTIONS } from './common';
import { EMPLOYEES } from './redux.datasets';
import EmployeeCardView from './components/employeeCardView';
import ChangeView from './components/ChangeView';
import SelectBranch from './components/SelectBranch';
import SelectDepartment from './components/SelectDepartment';
import SelectDesignation from './components/SelectDesignation';
import SelectEmployeeCategory from './components/SelectEmployeeCategory';
import SelectGender from './components/SelectGender';
import SelectShift from './components/SelectShift';
import ActionList from './components/ActionList';
import ExportAllExcel from './components/exportAllExcel';
import BasicMenu from 'common/components/tableHandlar/rowhandlar';
import { Button } from '@mui/material';
import dayjs from 'dayjs';

const propTypes = {};

const defaultProps = {
    entity: EMPLOYEES,
    domain: DOMAIN_NAME,
    loading: true
};

const DEFAULT_TABLE_DATA = {
    'fullName': {
        label: 'Full Name',
        isVisible: true,
        disabledChange: true,
        type: 'string'
    },
    'designationName': {
        label: 'Designation',
        isVisible: true,
        disabledChange: false,
        type: 'string'
    },
    'email': {
        label: 'Email',
        isVisible: true,
        disabledChange: true,
        type: 'string'
    },
    'branchName': {
        label: 'Branch',
        isVisible: true,
        disabledChange: false,
        type: 'string'
    },
    'joiningDate': {
        label: 'Joining Date',
        isVisible: false,
        disabledChange: false,
        type: 'date'
    },
    'employeeId': {
        label: 'Employee Id',
        isVisible: false,
        disabledChange: false,
        type: 'string'
    },
    'resignationDate': {
        label: 'Resignation Date',
        isVisible: false,
        disabledChange: false,
        type: 'date'
    }
};

// MUI string avatar function
function stringAvatar(name) {
    const nameParts = name.split(' ').filter((part) => part.length > 0);
    return {
        sx: { color: '#ffffff', width: 32, height: 32, fontSize: 16 },
        children:
            nameParts.length >= 2
                ? `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase()
                : nameParts.length === 1
                ? nameParts[0][0].toUpperCase()
                : '?'
    };
}

const dataSelector = createCachedSelector(entitySelector,
    (resp) => ((resp && resp.data) ? (resp.data.map(
        (obj) => {
            let data = (({ user, branch, designation, ...rest }) => ({ ...rest, email: user.email, branchName: branch.name, designationName: designation.name }))(obj);
            const fullName = `${data.firstname || ''} ${data.middlename || ''} ${data.lastname || ''}`.trim().replace(/\s+/g, ' ');
            const processedData = { ...data, fullName };
            return Object.fromEntries(Object.keys(DEFAULT_TABLE_DATA).map(
                (key) => [key, processedData[key]])
            )
        }
    )
    ) : [])
)((state, domain, entity) => `@data-${domain}-${entity}`);

const totalRowsSelector = createCachedSelector(entitySelector, (resp) =>
    resp.total ? parseInt(resp.total) : 0
)((state, domain, entity) => `@data-${domain}-${entity}`);

const mapState = (state, { domain, entity }) => ({
    data: dataSelector(state, domain, entity),
    loading: datasetLoadingSelector(state, domain, entity),
    totalRows: totalRowsSelector(state, domain, entity)
});

const mapDispatch = (dispatch, { entity }) => ({
    fetchDataset: ({ offset = "", limit = ROWS_PER_PAGE, shouldAppend = false, searchTerm, sortBy, sortDescending, branch, department, designation, employeeCategory, gender, shift }) => dispatch(
        createFetchPaginatedDataset({ entity: entity, domain: DOMAIN_NAME })({
            limit,
            offset,
            shouldAppend,
            searchTerm,
            ...(sortBy ? { 'sort-by': sortBy, 'sort-descending': sortDescending } : {}),
            filters: {
                ...(branch ? { branch } : {}),
                ...(department ? { department } : {}),
                ...(designation ? { designation } : {}),
                ...(employeeCategory ? { employeeCategory } : {}),
                ...(gender ? { gender } : {}),
                ...(shift ? { shift } : {})
            }
        })
    ),
});

// Build column definitions from DEFAULT_TABLE_DATA dynamically
const buildColumns = (tableData) =>
    Object.entries(tableData)
        .filter(([, v]) => v.isVisible)
        .map(([key, v]) => ({
            id: key,
            label: v.label,
            sortable: true,
            type: v.type
        }));

const EmployeeList = connect(
    mapState,
    mapDispatch
)(({ fetchDataset, data, totalRows = 0 }) => {
    useEffect(() => {
        fetchDataset({ offset: "" });
    }, [fetchDataset]);

    const [pageSize, setPageSize] = useState(ROWS_PER_PAGE);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState("");
    const [branchFilter, setBranchFilter] = useState(null);
    const [departmentFilter, setDepartmentFilter] = useState(null);
    const [designationFilter, setDesignationFilter] = useState(null);
    const [employeeCategoryFilter, setEmployeeCategoryFilter] = useState(null);
    const [genderFilter, setGenderFilter] = useState(null);
    const [shiftFilter, setShiftFilter] = useState(null);
    const [tableData, setTableData] = useState(DEFAULT_TABLE_DATA);
    const [view, setView] = useState("listView");
    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc');
    const navigate = useNavigate();

    const columns = buildColumns(tableData);

    const handleClick = ({ action, row }) => {
        if (action === 'edit') {
            navigate(`/employee-list/${row?.email?.split('@')[0]}`, { state: row });
        } else if (action === 'manage') {
            navigate(`/manage/${row?.email}`, { state: row });
        }
    };

    const handleChangePage = (event, newPage) => {
        fetchDataset({
            offset: (newPage - 1) * pageSize,
            limit: pageSize,
            searchTerm,
            sortBy: sortField,
            sortDescending: sortField ? sortDirection === 'desc' : undefined,
            branch: branchFilter, department: departmentFilter, designation: designationFilter,
            employeeCategory: employeeCategoryFilter, gender: genderFilter, shift: shiftFilter
        });
        setCurrentPage(newPage);
    };

    const handleRowsPerPageChange = (newPageSize) => {
        setPageSize(newPageSize);
        setCurrentPage(1);
        fetchDataset({
            offset: 0, limit: newPageSize, searchTerm,
            sortBy: sortField, sortDescending: sortField ? sortDirection === 'desc' : undefined,
            branch: branchFilter, department: departmentFilter, designation: designationFilter,
            employeeCategory: employeeCategoryFilter, gender: genderFilter, shift: shiftFilter
        });
    };

    const handleSearch = (value) => {
        setSearchTerm(value);
        setCurrentPage(1);
        fetchDataset({
            offset: '', limit: pageSize, searchTerm: value,
            sortBy: sortField, sortDescending: sortField ? sortDirection === 'desc' : undefined,
            branch: branchFilter, department: departmentFilter, designation: designationFilter,
            employeeCategory: employeeCategoryFilter, gender: genderFilter, shift: shiftFilter
        });
    };

    const makeFilterHandler = (setter, filterKey) => (value) => {
        setter(value);
        setCurrentPage(1);
        const newFilters = {
            branch: branchFilter, department: departmentFilter, designation: designationFilter,
            employeeCategory: employeeCategoryFilter, gender: genderFilter, shift: shiftFilter,
            [filterKey]: value
        };
        fetchDataset({
            offset: "", limit: pageSize, searchTerm,
            sortBy: sortField, sortDescending: sortField ? sortDirection === 'desc' : undefined,
            ...newFilters
        });
    };

    const handleBranchFilter = makeFilterHandler(setBranchFilter, 'branch');
    const handleDepartmentFilter = makeFilterHandler(setDepartmentFilter, 'department');
    const handleDesignationFilter = makeFilterHandler(setDesignationFilter, 'designation');
    const handleEmployeeCategoryFilter = makeFilterHandler(setEmployeeCategoryFilter, 'employeeCategory');
    const handleGenderFilter = makeFilterHandler(setGenderFilter, 'gender');
    const handleShiftFilter = makeFilterHandler(setShiftFilter, 'shift');

    const handleResetFilters = () => {
        setBranchFilter(null); setDepartmentFilter(null); setDesignationFilter(null);
        setEmployeeCategoryFilter(null); setGenderFilter(null); setShiftFilter(null);
        setSearchTerm(""); setSortField(null); setSortDirection('asc');
        setTableData(DEFAULT_TABLE_DATA); setView("listView"); setCurrentPage(1);
        fetchDataset({ offset: "", limit: pageSize, searchTerm: "" });
    };

    const handleSort = (field) => {
        let newDirection = 'asc';
        let newDescending = false;

        if (sortField === field && sortDirection === 'asc') {
            newDirection = 'desc';
            newDescending = true;
        } else if (sortField === field && sortDirection === 'desc') {
            setSortField(null); setSortDirection('asc');
            fetchDataset({
                offset: (currentPage - 1) * pageSize, limit: pageSize, searchTerm,
                branch: branchFilter, department: departmentFilter, designation: designationFilter,
                employeeCategory: employeeCategoryFilter, gender: genderFilter, shift: shiftFilter
            });
            return;
        }

        setSortField(field); setSortDirection(newDirection);
        fetchDataset({
            offset: (currentPage - 1) * pageSize, limit: pageSize, searchTerm,
            sortBy: field, sortDescending: newDescending,
            branch: branchFilter, department: departmentFilter, designation: designationFilter,
            employeeCategory: employeeCategoryFilter, gender: genderFilter, shift: shiftFilter
        });
    };

    // Async filter dropdowns passed as extraFiltersContent to CommonDataTable
    const extraFiltersContent = (
        <>
            <SelectBranch input={{ name: 'branch', onChange: handleBranchFilter, value: branchFilter }} />
            <SelectDepartment input={{ name: 'department', onChange: handleDepartmentFilter, value: departmentFilter }} />
            <SelectDesignation input={{ name: 'designation', onChange: handleDesignationFilter, value: designationFilter }} />
            <SelectEmployeeCategory input={{ name: 'employeeCategory', onChange: handleEmployeeCategoryFilter, value: employeeCategoryFilter }} />
            <SelectGender input={{ name: 'gender', onChange: handleGenderFilter, value: genderFilter }} />
            <SelectShift input={{ name: 'shift', onChange: handleShiftFilter, value: shiftFilter }} />
        </>
    );

    // Row renderer for list view
    const renderEmployeeRow = (row, idx) => {
        const fullName = row.fullName || '';
        return (
            <TableRow key={idx} sx={{ opacity: row.resignationDate ? 0.5 : 1 }}>
                {columns.map((col, colIdx) => {
                    const value = row[col.id];
                    if (col.id === 'fullName') {
                        return (
                            <TableCell key={col.id} sx={{ overflowWrap: 'break-word' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Avatar {...stringAvatar(fullName)} />
                                    <span>{fullName}</span>
                                </Box>
                            </TableCell>
                        );
                    }
                    if (col.type === 'date') {
                        return (
                            <TableCell key={col.id} sx={{ overflowWrap: 'break-word' }}>
                                {value ? dayjs(value).format('YYYY-MM-DD') : ''}
                            </TableCell>
                        );
                    }
                    return (
                        <TableCell key={col.id} sx={{ overflowWrap: 'break-word' }}>
                            {value}
                        </TableCell>
                    );
                })}
                <TableCell align='right'>
                    <ActionList row={row} handleClick={handleClick} />
                </TableCell>
            </TableRow>
        );
    };

    // For list view, add an Action column header at the end
    const listViewColumns = [
        ...columns,
        { id: '__action__', label: 'Action', sortable: false, align: 'right' }
    ];

    if (view === 'gridView') {
        return (
            <CommonDataTable
                title='Employee List'
                searchName='employee-email'
                searchValue={searchTerm}
                onSearch={handleSearch}
                extraActions={
                    <>
                        <ExportAllExcel
                            filters={{ branch: branchFilter, department: departmentFilter, designation: designationFilter, employeeCategory: employeeCategoryFilter, gender: genderFilter, shift: shiftFilter }}
                            searchTerm={searchTerm}
                            totalRows={totalRows}
                        />
                        <ChangeView view={view} setView={setView} />
                        <BasicMenu tableData={tableData} setTableData={setTableData} />
                    </>
                }
                extraFiltersContent={extraFiltersContent}
                onResetFilters={handleResetFilters}
                columns={[]}
                data={[]}
                showPagination={false}
            >
                {/* Card view rendered outside the table */}
                <Box sx={{ p: 2 }}>
                    <EmployeeCardView TABLE_DATA={Object.fromEntries(columns.map(c => [c.id, c.label]))} handleClick={handleClick} rows={data} ActionComponent={ActionList} />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
                    {/* Pagination for card view reuses same handler */}
                </Box>
            </CommonDataTable>
        );
    }

    return (
        <CommonDataTable
            title='Employee List'
            searchName='employee-email'
            searchValue={searchTerm}
            onSearch={handleSearch}
            extraActions={
                <>
                    <ExportAllExcel
                        filters={{ branch: branchFilter, department: departmentFilter, designation: designationFilter, employeeCategory: employeeCategoryFilter, gender: genderFilter, shift: shiftFilter }}
                        searchTerm={searchTerm}
                        totalRows={totalRows}
                    />
                    <ChangeView view={view} setView={setView} />
                    <BasicMenu tableData={tableData} setTableData={setTableData} />
                </>
            }
            extraFiltersContent={extraFiltersContent}
            onResetFilters={handleResetFilters}
            columns={listViewColumns}
            data={data}
            totalRows={totalRows}
            sortField={sortField}
            sortDescending={sortDirection === 'desc'}
            onSort={handleSort}
            page={currentPage}
            pageSize={pageSize}
            pageSizeOptions={ROWS_PER_PAGE_OPTIONS}
            pageSizeName='appliedLeaves'
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleRowsPerPageChange}
            renderRow={renderEmployeeRow}
        />
    );
});

EmployeeList.propTypes = propTypes;
EmployeeList.defaultProps = defaultProps;

export default EmployeeList;